import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import {
  deleteInstructorSlot,
  listInstructorBookings,
  listInstructorSlots,
  updateInstructorSlotStatus
} from "../../lib/leaderboardApi";
import "./InstructorSlotsCalendar.css";

function monthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function monthLabel(date) {
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function dayLabel(date) {
  return date.toLocaleDateString(undefined, { weekday: "short" });
}

function toDateKey(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function prettyTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "--";
  }
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function prettyDateTime(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "--";
  }
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function slotStatusClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "open") return "slot-status slot-status--open";
  if (normalized === "booked") return "slot-status slot-status--booked";
  if (normalized === "confirmed") return "slot-status slot-status--confirmed";
  if (normalized === "cancelled") return "slot-status slot-status--cancelled";
  if (normalized === "closed") return "slot-status slot-status--closed";
  if (normalized === "finished") return "slot-status slot-status--finished";
  return "slot-status";
}

function slotStatusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "open") return "Open";
  if (normalized === "closed") return "Closed";
  if (normalized === "finished") return "Finished";
  if (normalized === "cancelled") return "Cancelled";
  return normalized || "Unknown";
}

function slotStatusTransitions(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "open") return ["closed", "finished", "cancelled"];
  if (normalized === "closed") return ["open", "finished", "cancelled"];
  return [];
}

function buildMonthCells(baseMonth) {
  const start = monthStart(baseMonth);
  const startWeekday = start.getDay();
  const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), day));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

function InstructorSlotsCalendarPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [instructorName, setInstructorName] = useState("");
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [slotActionBusy, setSlotActionBusy] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);

  const baseMonth = useMemo(() => monthStart(new Date()), []);
  const monthBuckets = useMemo(
    () => [0, 1, 2].map((offset) => new Date(baseMonth.getFullYear(), baseMonth.getMonth() + offset, 1)),
    [baseMonth]
  );

  const activeMonth = monthBuckets[activeMonthIndex] || monthBuckets[0];
  const activeMonthCells = useMemo(() => buildMonthCells(activeMonth), [activeMonth]);

  const fromIso = useMemo(() => new Date(baseMonth.getFullYear(), baseMonth.getMonth(), 1).toISOString(), [baseMonth]);
  const toIso = useMemo(() => new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 3, 0, 23, 59, 59).toISOString(), [baseMonth]);

  const loadInstructorData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const user = await getCurrentUser();
      const attrs = await fetchUserAttributes();
      const sub = String(attrs?.sub || "").trim();
      const name = String(attrs?.preferred_username || user?.username || "Instructor").trim();
      const role = String(attrs?.["custom:userRole"] || "student").trim().toLowerCase();

      if (role !== "instructor") {
        navigate("/profile/my-bookings", { replace: true });
        return;
      }

      setInstructorId(sub);
      setInstructorName(name || "Instructor");
      if (!sub) {
        throw new Error("Missing user ID (sub). Please sign in again.");
      }

      const [slotItems, bookingItems] = await Promise.all([
        listInstructorSlots({ instructorId: sub, from: fromIso, to: toIso, sortDir: "asc" }),
        listInstructorBookings({ instructorId: sub, sortDir: "asc" })
      ]);

      setSlots(slotItems || []);
      setBookings(bookingItems || []);
      if ((slotItems || []).length > 0) {
        setSelectedSlotId((prev) => prev || slotItems[0].slotId || "");
      } else {
        setSelectedSlotId("");
      }
    } catch (loadError) {
      const message = loadError?.message || "Unable to load instructor slots";
      setError(message);
      setSlots([]);
      setBookings([]);
      if (/not authenticated|sign in|no current user/i.test(message)) {
        navigate("/auth/sign-in", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [fromIso, navigate, toIso]);

  useEffect(() => {
    loadInstructorData();
  }, [loadInstructorData]);

  async function handleSlotStatusToggle(nextStatus) {
    if (!selectedSlot?.slotId || !instructorId) return;

    setSlotActionBusy(`status:${selectedSlot.slotId}`);
    setError("");
    try {
      await updateInstructorSlotStatus({
        instructorId,
        slotId: selectedSlot.slotId,
        status: nextStatus
      });
      await loadInstructorData();
    } catch (actionError) {
      setError(actionError?.message || "Unable to update slot status");
    } finally {
      setSlotActionBusy("");
    }
  }

  async function handleRemoveSlot() {
    if (!selectedSlot?.slotId || !instructorId) return;

    const ok = window.confirm("Remove this slot? Active-booking slots cannot be removed.");
    if (!ok) return;

    setSlotActionBusy(`delete:${selectedSlot.slotId}`);
    setError("");
    try {
      await deleteInstructorSlot({ instructorId, slotId: selectedSlot.slotId });
      await loadInstructorData();
    } catch (actionError) {
      setError(actionError?.message || "Unable to remove slot");
    } finally {
      setSlotActionBusy("");
    }
  }

  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const slot of slots) {
      const key = toDateKey(slot.startAt);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(slot);
    }

    for (const daySlots of map.values()) {
      daySlots.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    }

    return map;
  }, [slots]);

  const bookingsBySlotId = useMemo(() => {
    const map = new Map();
    for (const booking of bookings) {
      if (!booking.slotId) continue;
      if (!map.has(booking.slotId)) map.set(booking.slotId, []);
      map.get(booking.slotId).push(booking);
    }

    for (const slotBookings of map.values()) {
      slotBookings.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
    }

    return map;
  }, [bookings]);

  const selectedSlot = useMemo(
    () => slots.find((slot) => slot.slotId === selectedSlotId) || null,
    [selectedSlotId, slots]
  );

  const selectedSlotBookings = useMemo(() => {
    if (!selectedSlot?.slotId) return [];
    return bookingsBySlotId.get(selectedSlot.slotId) || [];
  }, [bookingsBySlotId, selectedSlot]);

  const slotStats = useMemo(() => {
    const summary = { total: slots.length, open: 0, closed: 0, finished: 0, seatsBooked: 0 };
    for (const slot of slots) {
      const status = String(slot.status || "").toLowerCase();
      if (status === "open") summary.open += 1;
      if (status === "closed") summary.closed += 1;
      if (status === "finished") summary.finished += 1;
      summary.seatsBooked += Number(slot.bookedCount || 0);
    }
    return summary;
  }, [slots]);

  useEffect(() => {
    if (!selectedSlot?.status) {
      setStatusDraft("");
      return;
    }
    setStatusDraft(String(selectedSlot.status).toLowerCase());
  }, [selectedSlot?.slotId, selectedSlot?.status]);

  return (
    <section className="instructor-slot-page">
      <Helmet>
        <title>Instructor Slot Calendar | ArmorySync</title>
      </Helmet>

      <div className="instructor-slot-hero">
        <div>
          <h1>Instructor Slot Calendar</h1>
          <p>Manage booking lifecycle, monitor occupancy, and review students in one place.</p>
          <div className="slot-hero-stats">
            <span>{slotStats.total} Total</span>
            <span>{slotStats.open} Open</span>
            <span>{slotStats.closed} Closed</span>
            <span>{slotStats.finished} Finished</span>
            <span>{slotStats.seatsBooked} Seats Booked</span>
          </div>
        </div>
        <div className="instructor-slot-actions">
          <Link className="detail-link" to="/profile">Back to Profile</Link>
          <button type="button" className="detail-link detail-link--button" onClick={loadInstructorData} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {loading && <p className="instructor-slot-state">Loading slots...</p>}
      {!loading && error && <p className="instructor-slot-state instructor-slot-state--error">{error}</p>}
      {!loading && !error && (
        <div className="instructor-slot-layout">
          <div className="instructor-slot-calendar-board">
            <div className="month-switcher">
              {monthBuckets.map((monthDate, idx) => (
                <button
                  key={`${monthDate.getFullYear()}-${monthDate.getMonth()}`}
                  type="button"
                  className={`month-switcher-btn ${idx === activeMonthIndex ? "active" : ""}`}
                  onClick={() => setActiveMonthIndex(idx)}
                >
                  {monthLabel(monthDate)}
                </button>
              ))}
            </div>

            <section className="instructor-month-card">
              <header className="instructor-month-header">
                <h2>{monthLabel(activeMonth)}</h2>
                <span>{instructorName || "Instructor"}</span>
              </header>

              <div className="instructor-week-header">
                {[0, 1, 2, 3, 4, 5, 6].map((offset) => (
                  <div key={offset}>{dayLabel(new Date(2026, 0, offset + 4))}</div>
                ))}
              </div>

              <div className="instructor-month-grid">
                {activeMonthCells.map((dateValue, idx) => {
                  if (!dateValue) {
                    return <div key={`blank-${idx}`} className="month-day month-day--blank" />;
                  }

                  const key = toDateKey(dateValue);
                  const daySlots = slotsByDay.get(key) || [];

                  return (
                    <div key={key} className="month-day">
                      <div className="month-day-top">
                        <strong>{dateValue.getDate()}</strong>
                        {daySlots.length > 0 && <span>{daySlots.length}</span>}
                      </div>

                      <div className="month-day-slots">
                        {daySlots.length === 0 && <p className="month-day-empty">No slots</p>}
                        {daySlots.map((slot) => {
                          const isSelected = selectedSlotId === slot.slotId;
                          return (
                            <button
                              type="button"
                              key={slot.slotId}
                              className={`day-slot-chip ${isSelected ? "is-selected" : ""}`}
                              onClick={() => setSelectedSlotId(slot.slotId)}
                              title={`${slot.title || "Private Session"} - ${prettyDateTime(slot.startAt)}`}
                            >
                              <span>{prettyTime(slot.startAt)}</span>
                              <span>{slot.title || "Session"}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <aside className="instructor-slot-card">
            {!selectedSlot && <p>Select a slot to review status, occupancy, and students.</p>}

            {selectedSlot && (
              <>
                <div className="slot-detail-head">
                  <h2>{selectedSlot.title || "Private Session"}</h2>
                  <span className={slotStatusClass(selectedSlot.status)}>{selectedSlot.status || "unknown"}</span>
                </div>

                <div className="slot-detail-actions">
                  {(() => {
                    const transitions = slotStatusTransitions(selectedSlot.status);
                    const canApplyStatus = transitions.includes(statusDraft);
                    return (
                      <div className="slot-status-compact">
                        <label htmlFor="slot-status-select">Status</label>
                        <div className="slot-status-compact-row">
                          <select
                            id="slot-status-select"
                            value={statusDraft}
                            onChange={(event) => setStatusDraft(event.target.value)}
                            disabled={slotActionBusy.length > 0 || transitions.length === 0}
                          >
                            <option value={String(selectedSlot.status || "").toLowerCase()}>
                              {slotStatusLabel(selectedSlot.status)} (Current)
                            </option>
                            {transitions.map((status) => (
                              <option key={status} value={status}>
                                {slotStatusLabel(status)}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="slot-action-btn slot-action-btn--status"
                            onClick={() => handleSlotStatusToggle(statusDraft)}
                            disabled={!canApplyStatus || slotActionBusy.length > 0}
                          >
                            {slotActionBusy === `status:${selectedSlot.slotId}` ? "Updating..." : "Apply"}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="slot-detail-grid">
                  <div>
                    <span>Start</span>
                    <strong>{prettyDateTime(selectedSlot.startAt)}</strong>
                  </div>
                  <div>
                    <span>End</span>
                    <strong>{prettyDateTime(selectedSlot.endAt)}</strong>
                  </div>
                  <div>
                    <span>Seats</span>
                    <strong>{`${selectedSlot.bookedCount || 0}/${selectedSlot.capacity || 0}`}</strong>
                  </div>
                  <div>
                    <span>Class Type</span>
                    <strong>{selectedSlot.classType || "private"}</strong>
                  </div>
                  <div>
                    <span>Range</span>
                    <strong>{selectedSlot.rangeName || "-"}</strong>
                  </div>
                </div>

                <div className="slot-danger-zone">
                  <span>Slot Actions</span>
                  <button
                    type="button"
                    className="slot-action-btn slot-action-btn--remove"
                    onClick={handleRemoveSlot}
                    disabled={slotActionBusy.length > 0}
                  >
                    {slotActionBusy === `delete:${selectedSlot.slotId}` ? "Removing..." : "Remove Slot"}
                  </button>
                </div>

                <h3>Students</h3>
                {selectedSlotBookings.length === 0 && <p className="instructor-slot-state">No bookings for this slot yet.</p>}
                {selectedSlotBookings.length > 0 && (
                  <div className="student-list">
                    {selectedSlotBookings.map((booking) => (
                      <article key={booking.bookingId} className="student-list-item">
                        <header>
                          <div className="student-headline">
                            <span className="student-avatar">{(booking.studentName || "U").trim().charAt(0).toUpperCase()}</span>
                            <strong>{booking.studentName || "Unknown student"}</strong>
                          </div>
                          <span className={slotStatusClass(booking.status)}>{booking.status || "booked"}</span>
                        </header>
                        <p>{booking.notes || "No notes"}</p>
                      </article>
                    ))}
                  </div>
                )}
              </>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}

export const InstructorSlotsCalendar_Page = {
  path: "/profile/instructor-slots",
  element: <InstructorSlotsCalendarPage />
};
